import { IonContent, IonPage, IonButton, IonIcon } from '@ionic/react';
import { logInOutline } from 'ionicons/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import movementsImg from '../assets/images/movements.svg';
import peopleCountImg from '../assets/images/people-count.svg';
import multipleCameras from '../assets/images/multiple-cameras.svg';
import heapMap from '../assets/images/heatmap.svg';
import securityImg from '../assets/images/security.svg';

function isMobileApp() {
    return !!(window as any).Capacitor;
}

// Hook para detectar tamanho da tela
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    return isMobile;
}

const features = [
    {
        img: peopleCountImg,
        title: "Contagem de Pessoas",
        desc: "Conte quantas pessoas entram e saem da sua loja em tempo real. Não precisa instalar sensores ou equipamentos extras: tudo funciona direto pelas câmeras, sem dor de cabeça.",
    },
    {
        img: movementsImg,
        title: "Histórico de Movimento",
        desc: "Veja o histórico de movimento da sua loja nos últimos dias. Oferecemos um gráfico detalhado mostrando a quantidade de pessoas ao longo do tempo, facilitando a análise de horários de pico e tendências de fluxo.",
    },
    {
        img: heapMap,
        title: "Mapa de Calor da Loja",
        desc: "Oferecemos um mapa de calor que mostra as áreas mais frequentadas da sua loja, ajudando você a visualizar onde os clientes mais circulam em tempo real e tomar decisões estratégicas para o seu negócio.",
    },
    {
        img: multipleCameras,
        title: "Compatível com Qualquer Câmera",
        desc: "Nossa solução funciona com qualquer câmera IP ou analógica conectada ao sistema, sem necessidade de equipamentos proprietários. Compatível com qualquer resolução, inclusive câmeras de 360p. Transforme qualquer câmera em um sensor inteligente de monitoramento e análise."
    },
    {
        img: securityImg,
        title: "Segurança Total",
        desc: "Autenticação segura, proteção dos seus dados e criptografia de ponta a ponta para garantir a privacidade das informações.",
    }
];

const partners = [
    {
        name: "Azure",
        logo: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg"
    },
    {
        name: "Auth0",
        logo: "https://cdn.auth0.com/styleguide/latest/lib/logos/img/badge.png"
    },
    {
        name: "YOLO AI",
        logo: "https://cdn.prod.website-files.com/646dd1f1a3703e451ba81ecc/64994922cf2a6385a4bf4489_UltralyticsYOLO_mark_blue.svg"
    },
    {
        name: "Cloudflare",
        logo: "https://upload.wikimedia.org/wikipedia/commons/4/4b/Cloudflare_Logo.svg"
    }
];

const Landing: React.FC = () => {
    const { loginWithRedirect } = useAuth0();
    const history = useHistory();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobileApp()) {
            history.replace('/login');
        }
    }, [history]);

    return (
        <IonPage>
            <IonContent fullscreen style={{ '--background': '#fff', padding: 0 }}>
                {/* HERO SECTION */}
                <section style={{
                    width: '100%',
                    minHeight: '40vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(120deg, #3880ff 60%, #fff 100%)',
                    color: '#fff',
                    padding: isMobile ? '20px 20px 24px 20px' : '24px 0 24px 0' // Reduzido: 40px→20px (mobile), 48px→24px (desktop)
                }}>
                    <img src="/favicon.png" alt="Logo" style={{ width: isMobile ? 48 : 64, marginBottom: isMobile ? 16 : 20 }} />
                    <h1 style={{
                        fontSize: isMobile ? 24 : 28,
                        fontWeight: 700,
                        marginBottom: isMobile ? 12 : 14,
                        textAlign: 'center',
                        letterSpacing: -0.3,
                        maxWidth: isMobile ? 280 : 600
                    }}>
                        Trackento Monitoramento
                    </h1>
                    <p style={{
                        fontSize: isMobile ? 14 : 16,
                        maxWidth: isMobile ? 320 : 500,
                        textAlign: 'center',
                        margin: isMobile ? '0 auto 24px auto' : '0 auto 28px auto',
                        color: '#eaf1fb',
                        lineHeight: 1.4
                    }}>
                        Plataforma inteligente para monitoramento de câmeras, análise de fluxo de pessoas e detecção automática usando IA.
                    </p>
                    <IonButton
                        size={isMobile ? 'default' : 'large'}
                        color="light"
                        style={{
                            color: '#3880ff',
                            fontWeight: 600,
                            fontSize: isMobile ? 14 : 15,
                            padding: isMobile ? '0 20px' : '0 24px',
                            height: isMobile ? '36px' : '40px'
                        }}
                        onClick={() => loginWithRedirect()}
                    >
                        <IonIcon slot="start" icon={logInOutline} style={{ fontSize: isMobile ? 16 : 18 }} />
                        Comece Agora
                    </IonButton>
                </section>

                {/* FEATURES SECTION */}
                <section
                    style={{
                        width: '100%',
                        background: 'linear-gradient(225deg, #eaf1fb 60%, #f6f8fa 100%, #3880ff 0%)',
                        padding: isMobile ? '40px 20px 20px 20px' : '48px 0 24px 0',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* SVG gradiente decorativo canto inferior esquerdo */}
                    <svg
                        width="420"
                        height="280"
                        viewBox="0 0 420 280"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            position: 'absolute',
                            left: 0,
                            bottom: 0,
                            zIndex: 0,
                            pointerEvents: 'none',
                        }}
                    >
                        <defs>
                            <radialGradient id="leftGradient" cx="0.2" cy="0.8" r="1">
                                <stop offset="0%" stopColor="#3880ff" stopOpacity="0.25" />
                                <stop offset="100%" stopColor="#eaf1fb" stopOpacity="0" />
                            </radialGradient>
                        </defs>
                        <ellipse cx="140" cy="240" rx="180" ry="60" fill="url(#leftGradient)" />
                    </svg>
                    <div style={{ maxWidth: isMobile ? '100%' : 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
                        {features.map((f, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : (idx % 2 === 0 ? 'row' : 'row-reverse'),
                                    alignItems: 'center',
                                    gap: isMobile ? 20 : 40,
                                    marginBottom: isMobile ? 32 : 40,
                                    background: '#fff',
                                    borderRadius: 12,
                                    boxShadow: '0 1px 12px 0 rgba(56,128,255,0.06)',
                                    padding: isMobile ? 16 : 24,
                                }}
                            >
                                <img
                                    src={f.img}
                                    alt={f.title}
                                    style={{
                                        maxWidth: isMobile ? 160 : 220,
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        background: 'none',
                                        flexShrink: 0,
                                        display: 'block'
                                    }}
                                />
                                <div style={{ flex: 1, minWidth: 180, textAlign: isMobile ? 'center' : 'left' }}>
                                    <h3 style={{
                                        fontSize: isMobile ? 18 : 22,
                                        fontWeight: 700,
                                        margin: 0,
                                        color: '#222',
                                        lineHeight: 1.2
                                    }}>{f.title}</h3>
                                    <p style={{
                                        fontSize: isMobile ? 14 : 15,
                                        color: '#555',
                                        margin: isMobile ? '10px 0 0 0' : '12px 0 0 0',
                                        lineHeight: 1.5
                                    }}>{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SOBRE SECTION */}
                <section style={{
                    maxWidth: isMobile ? '100%' : 700,
                    margin: '0 auto',
                    padding: isMobile ? '24px 20px 32px 20px' : '24px 0 40px 0',
                    textAlign: 'center'
                }}>
                    <h2 style={{
                        fontSize: isMobile ? 20 : 24,
                        fontWeight: 700,
                        marginBottom: isMobile ? 12 : 14,
                        color: '#222'
                    }}>Sobre o Trackento</h2>
                    <p style={{
                        fontSize: isMobile ? 14 : 15,
                        color: '#333',
                        maxWidth: isMobile ? '100%' : 600,
                        margin: '0 auto',
                        lineHeight: 1.5,
                        fontWeight: 400,
                        letterSpacing: 0.1,
                        fontFamily: 'inherit'
                    }}>
                        O Trackento é uma solução moderna para monitoramento de ambientes, integrando câmeras IP, inteligência artificial e nuvem. Receba alertas automáticos, visualize relatórios e mantenha seu espaço seguro com tecnologia de ponta.
                    </p>
                </section>

                {/* CALL TO ACTION */}
                <section style={{
                    width: '100%',
                    background: 'linear-gradient(120deg, #3880ff 60%, #f8f9fb 100%)',
                    color: '#fff',
                    padding: isMobile ? '32px 20px' : '36px 0',
                    textAlign: 'center'
                }}>
                    <h2 style={{
                        fontSize: isMobile ? 18 : 20,
                        fontWeight: 700,
                        marginBottom: isMobile ? 14 : 16
                    }}>Pronto para monitorar com inteligência?</h2>
                    <IonButton
                        size={isMobile ? 'default' : 'large'}
                        color="light"
                        style={{
                            color: '#3880ff',
                            fontWeight: 600,
                            fontSize: isMobile ? 14 : 15,
                            padding: isMobile ? '0 20px' : '0 24px',
                            height: isMobile ? '36px' : '40px'
                        }}
                        onClick={() => loginWithRedirect()}
                    >
                        <IonIcon slot="start" icon={logInOutline} style={{ fontSize: isMobile ? 16 : 18 }} />
                        Comece Agora
                    </IonButton>
                </section>

                {/* COPYRIGHT */}
                <footer style={{
                    width: '100%',
                    background: '#f6f8fa',
                    color: '#888',
                    textAlign: 'center',
                    padding: isMobile ? '28px 20px 20px 20px' : '32px 0 20px 0',
                    fontSize: isMobile ? 14 : 15,
                    letterSpacing: 0.1,
                    marginTop: 0
                }}>
                    <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                        <span style={{
                            fontWeight: 700,
                            color: '#222',
                            fontSize: isMobile ? 14 : 15,
                            display: 'block',
                            marginBottom: isMobile ? 12 : 14
                        }}>
                            Tecnologias integradas:
                        </span>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: isMobile ? 20 : 24,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginTop: 0,
                            marginBottom: 6,
                            minHeight: isMobile ? 40 : 50
                        }}>
                            {partners.map((p, idx) => (
                                <img
                                    key={idx}
                                    src={p.logo}
                                    alt={p.name}
                                    style={{
                                        height: isMobile ? 28 : 32,
                                        maxWidth: isMobile ? 80 : 100,
                                        objectFit: 'contain',
                                        opacity: 0.7,
                                        background: 'transparent',
                                        borderRadius: 4,
                                        padding: isMobile ? '4px 8px' : '6px 10px',
                                        border: '1px solid #e1e8ed',
                                        transition: 'all 0.2s ease',
                                        filter: 'grayscale(0.3)',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <div style={{ fontSize: isMobile ? 12 : 13, color: '#888', marginTop: 12 }}>
                        © {new Date().getFullYear()} Trackento. Todos os direitos reservados.
                    </div>
                </footer>
            </IonContent>
        </IonPage>
    );
};

export default Landing;